import { useNavigate, useSearch } from '@tanstack/react-router';
import { Briefcase, ChevronLeft, ChevronRight, FileText, Users, type LucideIcon } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import { SEARCH_MIN_CHARS, SEARCH_TYPES, SEARCH_TYPE_LABELS, type SearchHit, type SearchType } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { SearchInput } from '../../../components/Input';
import { Segmented } from '../../../components/Segmented';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ServerErrorState } from '../../../components/system-states/SystemState';
import { formatNumber, initialsOf } from '../../../lib/format';
import { searchScopeLabel, useModules } from '../../../lib/modules';
import { DeepLink } from '../../notifications/components/DeepLink';
import { CandidatePopup, type CandidatePanelTab } from '../../candidates/components/panel/CandidatePanel';
import { useGlobalSearch } from '../api/pools-api';
import styles from '../Pools.module.css';
import hitStyles from './SearchResultsPage.module.css';

export interface SearchResultsUrl {
  readonly q?: string;
  readonly type?: SearchType;
  readonly page?: string;
}

export function readSearchResults(search: Readonly<Record<string, unknown>>): SearchResultsUrl {
  const type = SEARCH_TYPES.find((kind) => kind === search.type);
  return {
    ...(typeof search.q === 'string' && search.q !== '' ? { q: search.q } : {}),
    ...(type ? { type } : {}),
    ...(typeof search.page === 'string' && /^\d+$/.test(search.page) ? { page: search.page } : {}),
  };
}

const MATCH_LABELS: Readonly<Record<SearchHit['matchedOn'], string>> = {
  id: 'Reference or contact match',
  name_prefix: 'Name starts with it',
  name: 'Name',
  skill: 'Skill or tag',
  text: 'Profile or form answers',
};

const PAGE_SIZE = 25;

/** Everything but a person shows its kind as an icon (Search-results.dc.html). */
const KIND_ICONS: Readonly<Record<Exclude<SearchType, 'candidates'>, LucideIcon>> = {
  jobs: Briefcase,
  requisitions: FileText,
  pools: Users,
};

/** A plain left click opens the pop-up; Ctrl-click and the like keep the browser's new tab. */
function isPlainClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/**
 * F20.04's "see all results" page (§L10): the palette's five-per-kind becomes every match,
 * one kind at a time, ranked the same way. Only what the viewer may open is counted or shown.
 */
export function SearchResultsPage(): ReactNode {
  const modules = useModules();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as SearchResultsUrl;
  const query = search.q?.trim() ?? '';
  const page = Number(search.page ?? '1') > 1 ? Number(search.page) : 1;
  const overview = useGlobalSearch(query, 'all', 1, false);
  const groups = overview.data?.groups ?? [];
  const firstWithHits = groups.find((group) => group.total > 0)?.type ?? groups[0]?.type ?? 'candidates';
  const type: SearchType = search.type && groups.some((group) => group.type === search.type) ? search.type : firstWithHits;
  const results = useGlobalSearch(query, type, page, true);
  const [draft, setDraft] = useState(query);
  const [openId, setOpenId] = useState<string | null>(null);
  const [panelTab, setPanelTab] = useState<CandidatePanelTab>('cv');

  const go = (next: SearchResultsUrl): void => void navigate({ to: '/search', search: { ...next }, replace: true });
  const group = results.data?.groups.find((entry) => entry.type === type);
  const pageCount = Math.max(1, Math.ceil((group?.total ?? 0) / PAGE_SIZE));

  return (
    <div className={`${styles.page} ${styles.narrow}`}>
      <header className={styles.titleBlock}>
        <h1 className={styles.title}>Search</h1>
        <form
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            go({ q: draft.trim() });
          }}
        >
          <SearchInput aria-label={`Search ${searchScopeLabel(modules).toLowerCase()}`} value={draft} autoFocus placeholder="Name, reference, skill…" onChange={(event) => setDraft(event.target.value)} onClear={() => setDraft('')} />
        </form>
      </header>

      {query.length < SEARCH_MIN_CHARS ? (
        <EmptyState title="Type at least two letters" body={`Search finds ${searchScopeLabel(modules).toLowerCase()} you can open.`} action={null} />
      ) : overview.error && !overview.data ? (
        <ServerErrorState onRetry={() => void overview.refetch()} />
      ) : !overview.data ? (
        <SkeletonLines count={6} />
      ) : groups.every((entry) => entry.total === 0) ? (
        <EmptyState title={`Nothing matches "${query}"`} body="Check the spelling, try fewer letters, or search by a reference like JOB-2026-0012." action={null} />
      ) : (
        <>
          <Segmented
            label="Kind"
            value={type}
            options={SEARCH_TYPES.filter((kind) => groups.some((entry) => entry.type === kind)).map((kind) => ({
              value: kind,
              label: `${SEARCH_TYPE_LABELS[kind]} (${formatNumber(groups.find((entry) => entry.type === kind)?.total ?? 0)})`,
            }))}
            onChange={(value) => go({ q: query, type: value })}
          />
          <Card isFlush>
            {group && group.hits.length > 0 ? (
              <ul className={styles.hits}>
                {group.hits.map((hit) => (
                  <li key={hit.id} className={hitStyles.hit}>
                    <HitMark hit={hit} />
                    <span className={hitStyles.text}>
                      {hit.type === 'candidates' ? (
                        <a
                          href={hit.path}
                          className={hitStyles.name}
                          onClick={(event) => {
                            if (!isPlainClick(event)) return;
                            event.preventDefault();
                            setOpenId(hit.id);
                          }}
                        >
                          {hit.title}
                        </a>
                      ) : (
                        <DeepLink to={hit.path} className={hitStyles.name}>
                          {hit.title}
                        </DeepLink>
                      )}
                      <span className={hitStyles.meta}>{[hit.subtitle, MATCH_LABELS[hit.matchedOn]].filter(Boolean).join(' · ')}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState isInset title={`No ${SEARCH_TYPE_LABELS[type].toLowerCase()} match`} body="Try another kind above." action={null} />
            )}
          </Card>
          {pageCount > 1 && (
            <nav className={styles.pager} aria-label="Pages">
              <Button variant="ghost" icon={ChevronLeft} disabled={page <= 1} onClick={() => go({ q: query, type, page: String(page - 1) })}>
                Previous
              </Button>
              <span className={styles.meta}>
                Page {page} of {pageCount}
              </span>
              <Button variant="ghost" disabled={page >= pageCount} onClick={() => go({ q: query, type, page: String(page + 1) })}>
                Next
                <ChevronRight size={16} aria-hidden />
              </Button>
            </nav>
          )}
        </>
      )}
      {openId !== null && group && <SearchPopup candidateId={openId} hits={group.hits.filter((hit) => hit.type === 'candidates')} tab={panelTab} onTabChange={setPanelTab} onOpen={setOpenId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function HitMark({ hit }: { readonly hit: SearchHit }): ReactNode {
  if (hit.type === 'candidates') {
    return (
      <span className={hitStyles.mark} aria-hidden>
        {initialsOf(hit.title)}
      </span>
    );
  }
  const Icon = KIND_ICONS[hit.type];
  return (
    <span className={hitStyles.mark} aria-hidden>
      <Icon size={14} />
    </span>
  );
}

/** A person found opens in the candidate pop-up; Previous / Next walk this page of results. */
function SearchPopup({
  candidateId,
  hits,
  tab,
  onTabChange,
  onOpen,
  onClose,
}: {
  readonly candidateId: string;
  readonly hits: readonly SearchHit[];
  readonly tab: CandidatePanelTab;
  readonly onTabChange: (tab: CandidatePanelTab) => void;
  readonly onOpen: (id: string) => void;
  readonly onClose: () => void;
}): ReactNode {
  const index = hits.findIndex((hit) => hit.id === candidateId);
  const previous = index > 0 ? hits[index - 1] : undefined;
  const next = index >= 0 ? hits[index + 1] : undefined;
  return (
    <CandidatePopup
      candidateId={candidateId}
      tab={tab}
      onTabChange={onTabChange}
      onClose={onClose}
      {...(index >= 0
        ? {
            nav: {
              position: index + 1,
              total: hits.length,
              onPrevious: previous ? () => onOpen(previous.id) : null,
              onNext: next ? () => onOpen(next.id) : null,
            },
          }
        : {})}
    />
  );
}
